import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Light/dark handling.
 *
 * The token set in `app.css` keys dark mode off a `.dark` class rather than
 * `prefers-color-scheme`, which is what makes an explicit user choice possible
 * at all — a media query alone cannot be overridden. This provider owns that
 * class and keeps the choice in localStorage.
 *
 * 'system' is the default and stays live: it follows the OS rather than
 * resolving once at load, so a machine that switches theme at sunset takes the
 * console with it.
 */

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'ctrl:theme';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function apply(theme: Theme) {
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  document.documentElement.classList.toggle('dark', isDark);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? 'system',
  );

  useEffect(() => {
    apply(theme);

    if (theme !== 'system') return;

    // Keep following the OS while 'system' is selected.
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply('system');

    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
  }, []);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
